// ReportDetails.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import apiFileRequestCall from "../../../../apiHelper/filleHelper";
import Success from "../../../../formHelper/success";

/* small uid helper */
type Id = string;
const uid = () => Math.random().toString(36).slice(2);

/* Hardware item used in the UI */
interface HardwareItem {
  id: Id;
  name: string;
  amount: number | "";
  file?: File | null;
  preview?: string | null; // object URL or remote URL
  existingFileUrl?: string | null; // remote URL if provided by backend
  fileName?: string | null;
}

/* Company shape used for async select */
type CompanyEntry = {
  id: number | string;
  customer_name?: string;
  company_name?: string;
  address?: string;
  mobile_no?: string;
  city?: string;
  state?: { id?: number; name?: string } | null;
  other_state?: string | null;
  country?: { id?: number; name?: string } | null;
};

/* local error shapes */
type PerRowError = { [field: string]: string | undefined };
type FormError = {
  purchaseHardware?: { [index: number]: PerRowError };
} & { [key: string]: string | undefined };

interface Props {
  onNext?: () => void;
  onPrev?: () => void;
}

/* small responsive hook */
const useIsMobile = (maxWidth = 767.98) => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth <= maxWidth : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile("matches" in e ? e.matches : mq.matches);
    if (typeof mq.addEventListener === "function")
      mq.addEventListener("change", handler as any);
    else (mq as any).addListener(handler);
    setIsMobile(mq.matches);
    return () => {
      if (typeof mq.removeEventListener === "function")
        mq.removeEventListener("change", handler as any);
      else (mq as any).removeListener(handler);
    };
  }, [maxWidth]);
  return isMobile;
};

/* inject small CSS once */
const injectStyles = () => {
  const id = "rt-report-details-styles";
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const css = `
.supplier-table-root .rt-actions { display:flex; gap:8px; justify-content:flex-end; margin-top:12px; }
.supplier-table-root .hardware-row { display:grid; grid-template-columns: 1fr 96px 220px 64px; gap:10px; align-items:center; margin-bottom:10px; }
.supplier-table-root .file-preview-thumb{ max-width:180px; display:flex; align-items:center; gap:8px; }
.supplier-table-root .btn-icon.btn-sm { padding:6px; min-width:34px; height:34px; display:inline-flex; align-items:center; justify-content:center; }
.supplier-table-root .btn-warning.btn-sm { padding:6px 8px; border-radius:6px; }
.supplier-table-root .file-meta { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
@media (max-width:767.98px){
  .supplier-table-root .rt-actions{ flex-direction:column; }
  .supplier-table-root .hardware-row{ display:flex; flex-direction:column; gap:8px; align-items:stretch; }
  .supplier-table-root .file-preview-thumb{ max-width:100%; }
}
`;
  const style = document.createElement("style");
  style.id = id;
  style.innerHTML = css;
  document.head.appendChild(style);
};
export default function ReportDetails({ onNext, onPrev }: Props): JSX.Element {
  const isMobile = useIsMobile();
  const backendRoute = all_routes.backend;
  const latestFetchedRef = useRef<Record<string | number, CompanyEntry>>({});

  /* form state — consistent keys used everywhere (snake_case) */
  const [form, setForm] = useState({
    id: "",
    expanses_company_detail_id: "",
    company_name: "",
    company_id: "",
    order_no: "",
    purpose: "",
    purchaseHardware: [
      {
        id: uid(),
        name: "",
        amount: "",
        file: null,
        preview: null,
        existingFileUrl: null,
        fileName: null,
      } as HardwareItem,
    ] as HardwareItem[],
  });

  const purchaseHardwareRef = useRef<HardwareItem[]>(form.purchaseHardware);
  useEffect(() => {
    purchaseHardwareRef.current = form.purchaseHardware;
  }, [form.purchaseHardware]);

  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<FormError>({});
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [errors, setErrors] = useState<FormError>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");

  useEffect(() => injectStyles(), []);

  const setFormField = (k: string, v: any) =>
    setForm((p) => ({ ...p, [k]: v }));

  /* hardware helpers */
  const addHardware = () =>
    setForm((p) => ({
      ...p,
      purchaseHardware: [
        ...p.purchaseHardware,
        {
          id: uid(),
          name: "",
          amount: "",
          file: null,
          preview: null,
          existingFileUrl: null,
          fileName: null,
        },
      ],
    }));

  const removeHardware = (id: Id) =>
    setForm((p) => ({
      ...p,
      purchaseHardware:
        p.purchaseHardware.length > 1
          ? p.purchaseHardware.filter((x) => x.id !== id)
          : p.purchaseHardware,
    }));

  const updateHardware = (id: Id, patch: Partial<HardwareItem>) =>
    setForm((p) => ({
      ...p,
      purchaseHardware: p.purchaseHardware.map((x) =>
        x.id === id ? { ...x, ...patch } : x
      ),
    }));

  /* set file and manage object URLs correctly */
  const setHardwareFile = (id: Id, file: File | null) => {
    setForm((p) => ({
      ...p,
      purchaseHardware: p.purchaseHardware.map((x) => {
        if (x.id !== id) return x;

        // revoke previous preview if it looks like an object URL
        if (
          x.preview &&
          typeof x.preview === "string" &&
          x.preview.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(x.preview);
          } catch {}
        }

        const preview = file
          ? URL.createObjectURL(file)
          : x.existingFileUrl ?? null;

        return {
          ...x,
          file,
          preview,
          fileName: file ? file.name : x.fileName ?? null,
          existingFileUrl: file ? null : x.existingFileUrl ?? null,
        };
      }),
    }));
  };

  /* revoke any created object URLs on unmount */
  useEffect(() => {
    return () => {
      purchaseHardwareRef.current.forEach((h) => {
        if (
          h.preview &&
          typeof h.preview === "string" &&
          h.preview.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(h.preview);
          } catch {}
        }
      });
    };
  }, []);

  /* populate form from backend payload: maps backend uploaded_file -> purchaseHardware */
  const populateFromPayload = (payload: any) => {
    if (!payload) return;
    const p = payload?.data?.service_report ?? payload;
    const rows: HardwareItem[] =
      Array.isArray(p.uploaded_file) && p.uploaded_file.length
        ? p.uploaded_file.map((r: any) => ({
            id: r.rowId ?? uid(),
            name: r.name ?? "",
            amount: r.amount == null || r.amount === "" ? "" : Number(r.amount),
            file: null,
            fileName:
              r.fileName ?? (r.fileUrl ? r.fileUrl.split("/").pop() : null),
            existingFileUrl: r.fileUrl ?? null,
            preview: r.fileUrl ?? r.fileUrl ?? null,
          }))
        : [
            {
              id: uid(),
              name: "",
              amount: "",
              file: null,
              fileName: null,
              existingFileUrl: null,
              preview: null,
            },
          ];
    setForm((prev) => ({
      ...prev,
      id: p?.id ?? "",
      company_id: p?.company?.id ? String(p.company.id) : prev.company_id,
      company_name: p?.company?.company_name ?? prev.company_name,
      expanses_company_detail_id:
        payload?.data?.id ?? prev.expanses_company_detail_id,
      order_no: p.order_no ?? prev.order_no,
      purpose: p.purpose ?? prev.purpose,
      purchaseHardware: rows,
    }));
  };

  const parseErrors = (errors: Record<string, string>): FormError => {
    const result: FormError = {};
    for (const key in errors) {
      result[key] = errors[key];
    }
    return result;
  };
  /* initial load */
  const tryLoadCmp = async () => {
    try {
      const res = await apiRequestCall("post", backendRoute.getExpansesDetails);
      if (res?.data) populateFromPayload(res.data);
    } catch (err) {
      console.error("Failed to load details:", err);
    }
  };

  useEffect(() => {
    tryLoadCmp();
  }, []);

  const totals = useMemo(() => {
    const sum = form.purchaseHardware.reduce(
      (acc, h) => acc + (Number(h.amount) || 0),
      0
    );
    return { grand: sum };
  }, [form.purchaseHardware]);

  /* submit: builds FormData and includes files */
  const submit = async () => {
    setLoading(true);
    setFormError({});
    setErrors({});
    try {
      const fd = new FormData();
      const files: File[] = [];

      const fileUploadPayload = form.purchaseHardware.map((h) => {
        const fileIndex =
          h.file instanceof File ? files.push(h.file) - 1 : null;
        return {
          rowId: h.id,
          name: h.name || null,
          amount: h.amount === "" ? null : Number(h.amount),
          fileIndex,
          existingFileUrl: h.existingFileUrl ?? null,
          fileName: h.fileName ?? null,
        };
      });

      // append consistent keys backend expects
      fd.append("company_id", form.company_id || "");
      fd.append("order_no", form.order_no || "");
      fd.append("purpose", form.purpose || "");
      fd.append("file_upload", JSON.stringify(fileUploadPayload));
      fd.append("totals", JSON.stringify(totals));
      fd.append("id", form.id || "");
      fd.append(
        "expanses_company_detail_id",
        form.expanses_company_detail_id || ""
      );

      files.forEach((f) => fd.append("upload_file[]", f, f.name));

      const res = await apiFileRequestCall(
        "post",
        backendRoute.addUpdateServiceReport,
        { data: fd }
      );

      // handle validation response consistently
      if (res?.data?.code === 221) {
        setErrors(parseErrors(res?.data?.errors || {}));
        setLoading(false);
        return;
      }

      if (res?.data) populateFromPayload(res.data);
      tryLoadCmp();
      setShowSuccess(true);
      setShowMessage(res?.data?.message || "Saved successfully");
    } catch (err) {
      console.error("Save failed", err);
      setShowSuccess(false);
      alert("Save failed — check console for details.");
    } finally {
      setLoading(false);
    }
  };

  /* company search */
  const fetchCompanies = async (search: string, limit = 20) => {
    setLoadingCustomers(true);
    try {
      const res = await apiRequestCall("post", backendRoute.getCompanyDD, {
        search,
        limit,
      });
      const data = res?.data?.data;
      if (!Array.isArray(data)) return [];
      data.forEach((c: CompanyEntry) => {
        if (c && typeof c.id !== "undefined")
          latestFetchedRef.current[String(c.id)] = c;
      });
      return data.map((c: CompanyEntry) => ({
        value: c.id,
        label: c.company_name || c.customer_name || "-",
        raw: c,
      }));
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setLoadingCustomers(false);
    }
  };
  const loadOptions = (q: string) =>
    q?.trim().length < 2 ? Promise.resolve([]) : fetchCompanies(q, 20);

  const onCustomerChange = (opt: any) => {
    if (!opt) {
      setFormField("company_name", "");
      setFormField("company_id", "");
      return;
    }
    setErrors((p) => {
      const { company_id, ...rest } = p;
      return rest;
    });
    const idKey = String(opt.value ?? "");
    const sel: CompanyEntry = opt.raw ?? latestFetchedRef.current[idKey];
    setFormField("company_name", sel?.company_name ?? sel?.customer_name ?? "");
    setFormField("company_id", String(sel?.id ?? idKey));
  };

  // Controlled value for AsyncSelect — include raw when available
  const selectValue = form.company_id
    ? {
        value: form.company_id,
        label: form.company_name || "",
        raw: latestFetchedRef.current[String(form.company_id)] ?? undefined,
      }
    : null;

  return (
    <div className="supplier-table-root" style={{ padding: isMobile ? 12 : 0 }}>
      <div
        className={isMobile ? "rt-card" : "mb-1"}
        style={isMobile ? { marginBottom: 12 } : undefined}
      >
        <div className="mt-3 mb-2">
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Select Company <span className="text-danger">*</span>
            </label>
          </small>
          <AsyncSelect
            cacheOptions
            defaultOptions={false}
            value={selectValue as any}
            loadOptions={loadOptions}
            onChange={onCustomerChange}
            placeholder="Search company"
            isClearable
            noOptionsMessage={() =>
              loadingCustomers ? "Loading..." : "Type to search (min 2 chars)"
            }
          />
        </div>
        {errors.company_id && (
          <div className="text-danger small">{errors.company_id[0]}</div>
        )}

        <small>
          <label style={{ display: "block", fontWeight: 600 }}>
            Order No. <span className="text-danger">*</span>
          </label>
        </small>
        <input
          className="form-control mb-2"
          value={form.order_no}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFormField("order_no", e.target.value);
            setErrors((p) => {
              const { order_no, ...rest } = p;
              return rest;
            });
          }}
          placeholder="Order Number"
        />
        {errors.order_no && (
          <div className="text-danger small">{errors.order_no[0]}</div>
        )}
      </div>

      {/* <div
        className={isMobile ? "rt-card" : "mb-1"}
        style={isMobile ? { marginBottom: 12 } : undefined}
      >
        <small>
          <label style={{ display: "block", fontWeight: 600 }}>
            Upload Invoice <span className="text-danger">*</span>
          </label>
        </small>
        {errors.purchaseHardware && (
          <div className="text-danger small mb-2">
            {errors.purchaseHardware}
          </div>
        )}

        {form.purchaseHardware.map((r, i) => (
          <div key={r.id} className="hardware-row">
            <div>
              <input
                className="form-control"
                placeholder="Description"
                value={r.name}
                onChange={(e) => updateHardware(r.id, { name: e.target.value })}
              />
            </div>

            <div>
              <input
                type="number"
                className="form-control"
                placeholder="Amount"
                min={0}
                value={r.amount === "" ? "" : String(r.amount)}
                onChange={(e) =>
                  updateHardware(r.id, {
                    amount: e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "nowrap",
                minWidth: 0,
              }}
            >
              <input
                id={`file-${r.id}`}
                type="file"
                accept=".pdf,image/*"
                style={{ display: "none" }}
                onChange={(e) =>
                  setHardwareFile(
                    r.id,
                    e.target.files && e.target.files[0]
                      ? e.target.files[0]
                      : null
                  )
                }
              />

              <label htmlFor={`file-${r.id}`} style={{ margin: 0 }}>
                <span
                  className="btn btn-warning d-inline-flex align-items-center gap-2 btn-sm"
                  style={{
                    borderRadius: 6,
                    cursor: "pointer",
                    padding: "6px 8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {r.file || r.existingFileUrl ? (
                    <i className="feather-edit" />
                  ) : (
                    "Choose File"
                  )}
                </span>
              </label>

              {(r.preview || r.existingFileUrl) && (
                <button
                  type="button"
                  className="btn btn-danger d-inline-flex align-items-center gap-1 btn-sm"
                  onClick={() => setHardwareFile(r.id, null)}
                  title="Remove file"
                  style={{
                    borderRadius: 6,
                    padding: "6px 8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="feather-trash" />
                </button>
              )}

              <div
                style={{
                  flex: "0 0 72px",
                  height: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  borderRadius: 6,
                  background: "transparent",
                  minWidth: 0,
                }}
              >
                {r.preview &&
                  r.file &&
                  r.file.type &&
                  r.file.type.startsWith("image/") && (
                    <img
                      src={r.preview}
                      alt={r.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        borderRadius: 6,
                      }}
                    />
                  )}
                {r.existingFileUrl && (
                  <img
                    src={r.existingFileUrl}
                    alt={r.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      borderRadius: 6,
                    }}
                  />
                )}
              </div>
            </div>

            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-danger"
                onClick={() => removeHardware(r.id)}
                aria-label="remove"
                disabled={form.purchaseHardware.length === 1}
              >
                <i className="feather-trash" />
              </button>
              {i === form.purchaseHardware.length - 1 && (
                <button
                  type="button"
                  className="btn btn-icon btn-sm btn-soft-info"
                  onClick={addHardware}
                >
                  <i className="feather-plus" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div> */}

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ color: "red", flex: 1, minWidth: 0 }}></div>
        <div
          style={{
            width: isMobile ? "100%" : 320,
            minWidth: 0,
            border: "1px solid var(--rt-border-color, rgba(0,0,0,0.06))",
            borderRadius: 6,
          }}
        >
          <div
            style={{
              border: "1px solid var(--rt-border-color)",
              borderRadius: 6,
              padding: 10,
              background: "var(--rt-card-bg)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid var(--rt-border-color)",
                paddingTop: 8,
                marginTop: 8,
              }}
            >
              <h5>
                <span>Total</span>
              </h5>
              <strong>₹{totals.grand.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="rt-actions">
        <button
          type="button"
          className="btn btn-soft-warning"
          style={{ width: isMobile ? "100%" : undefined }}
          onClick={onPrev}
        >
          <i className="ti ti-arrow-left" />{" "}
          <span style={{ textTransform: "lowercase" }}>back</span>
        </button>
        <button
          type="button"
          className="btn btn-soft-success"
          onClick={submit}
          style={{ width: isMobile ? "100%" : undefined }}
          disabled={loading}
        >
          <i className="ti ti-check" /> Save
        </button>
      </div>
      <Success
        id="CustomerOrderSuccessModal"
        title="Service Report"
        message={showMessage}
        show={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
    </div>
  );
}
